/**
 * claimIdentifiers -- the ONE parser and the ONE wording for "reserve a device to a site
 * before it checks in" (device pre-registration), shared by the Sites page "Pre-register
 * devices" dialog and the Onboard page "Claim new devices" box.
 *
 * WHY (UI review 2026-10-17): the two screens disagreed. Sites said "MAC or serial", Onboard
 * said "MAC only". What the backend actually does decides it:
 *   * POST /sites/<id>/preregistrations (api/v2/prereg.py) stores ANY non-blank string,
 *     normalised to alnum-lowercase (services/prereg.normalize_identifier).
 *   * The reservation is only ever CONSUMED by services/prereg.match_and_claim, called from
 *     the owgw sync auto-adopt path, which matches it against Device.serial / Device.mac_address.
 *     An auto-adopted device's serial IS its colon-less MAC (owgw serialNumber), and the
 *     auto-adopt path only creates a device when that serial parses as a MAC.
 * So a vendor label serial (RAP750..., RSW226...) is accepted by the API but can never match:
 * it is a dead reservation. The truthful UI is "MAC address only", and both screens now refuse
 * anything that is not a MAC before it is sent, instead of silently storing a dead row.
 *
 * Accepted MAC spellings mirror the server's normaliser (it strips every non-alnum
 * character): aa:bb:cc:dd:ee:ff, AA-BB-CC-DD-EE-FF, aabb.ccdd.eeff, aabbccddeeff.
 *
 * No React, no fetch: unit-tested in claimIdentifiers.test.ts (`npm run test:unit`).
 */

/** Placeholder for both claim textareas: documentation-only, locally administered MACs. */
export const CLAIM_PLACEHOLDER = "02:00:00:00:00:01\n020000000002";

/** Field label used by both screens. */
export const CLAIM_LABEL = "MAC addresses (one per line, or comma-separated)";

/** The shared hint under both textareas -- the one true statement of what matches. */
export const CLAIM_HINT =
  "Use the device MAC address, not the vendor serial printed on the label (e.g. RAP750...). " +
  "Devices report their MAC as their serial, so a vendor serial never matches. " +
  "Colons or dashes optional.";

export interface ParsedClaim {
  /** Entries that are a MAC, as typed (trimmed). The server keeps the raw text for display. */
  macs: string[];
  /** Entries that are NOT a MAC -- the screen refuses to send while any are present. */
  invalid: string[];
}

/** Split a pasted blob the same way on both screens: commas and any whitespace; drop empties. */
export function splitClaimInput(text: string): string[] {
  return text.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);
}

/** True when `token` is a MAC once separators are removed (12 hex digits). */
export function isMacIdentifier(token: string): boolean {
  // Same stripping as services/prereg.normalize_identifier (every non-alnum char goes).
  const key = token.replace(/[^0-9A-Za-z]/g, "");
  return /^[0-9A-Fa-f]{12}$/.test(key);
}

/** Parse the pasted text into usable MACs and refused entries. Duplicates are kept once. */
export function parseClaimInput(text: string): ParsedClaim {
  const macs: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  for (const token of splitClaimInput(text)) {
    if (!isMacIdentifier(token)) {
      invalid.push(token);
      continue;
    }
    // De-duplicate on the normalised key so "aa:bb..." and "AABB..." are one reservation.
    const key = token.replace(/[^0-9A-Za-z]/g, "").toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    macs.push(token);
  }
  return { macs, invalid };
}

/** The refusal line shown when entries are not MACs, or null when everything is usable. */
export function invalidClaimMessage(parsed: ParsedClaim): string | null {
  if (parsed.invalid.length === 0) return null;
  const shown = parsed.invalid.slice(0, 3).join(", ");
  const more = parsed.invalid.length > 3 ? ` and ${parsed.invalid.length - 3} more` : "";
  return `Not a MAC address: ${shown}${more}. Remove or correct ${
    parsed.invalid.length === 1 ? "it" : "them"} to continue.`;
}
